<template>
    <div class="mb-[100px]">
        <div class="mb-[27px] mt-[42px] flex h-[42px] justify-between">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                <span class="flex flex-wrap items-center gap-2">
                    <span class="flex items-center gap-2">
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="getDomainStatusDotClass(domainState)"
                        ></span>
                        <span class="text-gray-800">
                            {{ reportedDomain.domain }}
                        </span>
                    </span>
                </span>
            </h1>
        </div>

        <BasicTabs :tabs="tabs" :tab-key="'domain-dmarc-management'" />

        <!-- Subdomain inheriting notice -->
        <div v-if="showSubdomainNotice" class="mt-[18px]">
            <div
                class="flex w-full items-center justify-between gap-[19px] rounded-[6.75px] border border-[#63B3ED] bg-[#EFF6FF] px-[14px] py-[14px]"
            >
                <div class="flex min-w-0 items-center gap-[19px]">
                    <span class="shrink-0" aria-hidden="true">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5ZM8.25 9.75C8.25 9.55109 8.32902 9.36032 8.46967 9.21967C8.61032 9.07902 8.80109 9 9 9H9.253C9.51515 8.99989 9.77398 9.05867 10.0104 9.17201C10.2468 9.28536 10.4547 9.45036 10.6187 9.65483C10.7828 9.8593 10.8988 10.098 10.9582 10.3534C11.0176 10.6087 11.0189 10.8741 10.962 11.13L10.502 13.196C10.4939 13.2326 10.4941 13.2706 10.5027 13.3071C10.5113 13.3436 10.5279 13.3777 10.5514 13.4069C10.575 13.4361 10.6048 13.4596 10.6386 13.4757C10.6725 13.4918 10.7095 13.5001 10.747 13.5H11C11.1989 13.5 11.3897 13.579 11.5303 13.7197C11.671 13.8603 11.75 14.0511 11.75 14.25C11.75 14.4489 11.671 14.6397 11.5303 14.7803C11.3897 14.921 11.1989 15 11 15H10.747C10.4848 15.0001 10.226 14.9413 9.98963 14.828C9.75325 14.7146 9.54535 14.5496 9.38129 14.3452C9.21723 14.1407 9.10121 13.902 9.04179 13.6466C8.98237 13.3913 8.98108 13.1259 9.038 12.87L9.498 10.804C9.5061 10.7674 9.50586 10.7294 9.4973 10.6929C9.48874 10.6564 9.47208 10.6223 9.44856 10.5931C9.42503 10.5639 9.39524 10.5404 9.36139 10.5243C9.32754 10.5082 9.29049 10.4999 9.253 10.5H9C8.80109 10.5 8.61032 10.421 8.46967 10.2803C8.32902 10.1397 8.25 9.94891 8.25 9.75ZM10 7C10.2652 7 10.5196 6.89464 10.7071 6.70711C10.8946 6.51957 11 6.26522 11 6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5C9.73478 5 9.48043 5.10536 9.29289 5.29289C9.10536 5.48043 9 5.73478 9 6C9 6.26522 9.10536 6.51957 9.29289 6.70711C9.48043 6.89464 9.73478 7 10 7Z"
                                fill="#63B3ED"
                            />
                        </svg>
                    </span>
                    <p
                        class="min-w-0 font-inter text-[12.25px] font-normal not-italic leading-[142%] text-[#000]"
                    >
                        <span>
                            {{
                                $t(
                                    "vue.dmarc_management_subdomain_notice_line_inheriting_line1",
                                )
                            }}
                            <button
                                v-if="parentDomainExistInDmarceye"
                                type="button"
                                class="font-semibold text-[#088BEA] hover:underline hover:cursor-pointer"
                                @click="visitParentDomain"
                            >
                                {{ parentDomainDisplayName }}</button
                            ><span v-else class="font-semibold">{{
                                parentDomainDisplayName
                            }}</span
                            >.
                            {{
                                $t(
                                    "vue.dmarc_management_subdomain_notice_line_inheriting_line2",
                                )
                            }}
                        </span>
                    </p>
                </div>
                <AtomicButton
                    color="yellow"
                    size="sm"
                    class="inline-flex h-[28px] shrink-0 items-center justify-center rounded-[6px] border font-medium transition duration-100 ease-in-out hover:shadow-button focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:text-gray-100 focus:ring-[#3182CE] disabled:bg-gray-300 disabled:text-white px-[14px] text-[10.5px] leading-[140%]"
                    @click="
                        showInheritedDmarcConfig = !showInheritedDmarcConfig
                    "
                >
                    <template #icon>
                        <component
                            :is="
                                showInheritedDmarcConfig
                                    ? ChevronUp
                                    : ChevronDown
                            "
                            class="mr-2 h-4 w-4"
                        />
                    </template>
                    {{ $t("vue.dmarc_management_get_dmarc_configuration") }}
                </AtomicButton>
            </div>
        </div>

        <!-- Config sections -->
        <div class="mb-[150px] mt-[42px] space-y-12">
            <!-- ============ DOMAIN STATUS ============ -->
            <ConfigSection
                v-if="!showSubdomainNotice"
                :title="$t('vue.domain_status')"
                :description="$t('vue.domain_status_desc')"
            >
                <div class="grid grid-cols-3 gap-[24px] px-[21px] py-[21px]">
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.status") }}
                        </div>
                        <div class="mt-2">
                            <span
                                class="policy-badge"
                                :class="domainStatusBadgeClass"
                            >
                                {{ domainStatusLabel }}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.dmarc_policy") }}
                        </div>
                        <div class="mt-2">
                            <span
                                class="policy-badge capitalize"
                                :class="policyBadgeClass"
                            >
                                {{ policyDisplayLabel }}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.checked_at_date") }}
                        </div>
                        <div class="mt-2">
                            <span class="text-sm text-gray-500">
                                {{ recordStatusCheckedAt }}
                            </span>
                        </div>
                    </div>
                </div>
            </ConfigSection>

            <!-- ============ DMARC ============ -->
            <ConfigSection
                v-if="!showSubdomainNotice || showInheritedDmarcConfig"
                :title="$t('vue.dmarc_policy_reporting_title')"
                :description="$t('vue.dmarc_policy_reporting_desc')"
            >
                <div class="flex items-start gap-3 px-[21px] pt-[21px]">
                    <ShieldCheck
                        class="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                    />
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.your_current_dmarc_record") }}
                        </div>
                        <p
                            class="mt-0.5 text-[10.5px] leading-[142%] text-gray-500"
                        >
                            {{ $t("vue.your_current_dmarc_record_desc") }}
                        </p>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-[14px] px-[21px] py-[18px]">
                    <div
                        class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                    >
                        <div class="text-[10.5px] leading-[140%] text-gray-500">
                            {{ $t("vue.dmarc_policy") }}
                        </div>
                        <div
                            class="mt-1 text-[12px] font-semibold capitalize text-gray-800"
                        >
                            {{ dmarcPolicyStat }}
                        </div>
                    </div>
                    <div
                        class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                    >
                        <div class="text-[10.5px] leading-[140%] text-gray-500">
                            {{ $t("vue.policy_application_percentage") }}
                        </div>
                        <div
                            class="mt-1 text-[12px] font-semibold text-gray-800"
                        >
                            {{ dmarcPctStat }}
                        </div>
                    </div>
                    <div
                        class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                    >
                        <div class="text-[10.5px] leading-[140%] text-gray-500">
                            {{ $t("vue.reports_sent_to") }}
                        </div>
                        <div
                            class="mt-1 text-[12px] font-semibold text-gray-800"
                        >
                            {{
                                $t("vue.reports_address_count", {
                                    count: dmarcReportsCount,
                                })
                            }}
                        </div>
                    </div>
                </div>

                <!-- Success notice (collapsed state) -->
                <div
                    v-if="hasDmarcRecord && !showDmarcConfig"
                    class="px-[21px] pb-[18px]"
                >
                    <div class="notice-success">
                        <CheckCircle2
                            class="mt-[1px] h-[14px] w-[14px] shrink-0 text-[#00C951]"
                        />
                        <span>{{ $t("vue.dmarc_notice_configured") }}</span>
                    </div>
                </div>

                <!-- Expandable configuration -->
                <div
                    v-if="showDmarcConfig"
                    class="space-y-[28px] border-t border-gray-200 px-[21px] py-[24px]"
                >
                    <ManagedDmarcConfigurator
                        v-model="txtRecord"
                        host-name="_dmarc"
                    />

                    <!-- Step 3 - Publish (NEW Figma UI, existing logic reused) -->
                    <div>
                        <div class="mb-[14px] flex items-start gap-3">
                            <span class="step-badge">3</span>
                            <div>
                                <div
                                    class="text-[12px] font-semibold leading-[140%] text-gray-800"
                                >
                                    {{ $t("vue.step_publish_title") }}
                                </div>
                                <p
                                    class="text-[10.5px] leading-[142%] text-gray-500"
                                >
                                    {{ $t("vue.step_publish_desc") }}
                                </p>
                            </div>
                        </div>

                        <div
                            class="grid gap-[14px] max-[860px]:grid-cols-1"
                            :class="
                                isCloudflare ? 'grid-cols-3' : 'grid-cols-2'
                            "
                        >
                            <!-- Manually (informational only; value + copy live in step 2) -->
                            <div
                                class="rounded-md border border-gray-200 px-[16px] py-[16px]"
                            >
                                <div
                                    class="flex items-center gap-[6px] text-[12px] font-semibold text-gray-800"
                                >
                                    <Pencil
                                        class="h-[14px] w-[14px] text-gray-500"
                                    />
                                    {{ $t("vue.publish_manually_title") }}
                                </div>
                                <p
                                    class="mt-[6px] text-[10.5px] leading-[142%] text-gray-500"
                                >
                                    {{ $t("vue.publish_manually_desc") }}
                                </p>
                            </div>

                            <!-- By email -> opens popup that reuses existing EmailInstructions logic -->
                            <div
                                class="rounded-md border border-gray-200 px-[16px] py-[16px]"
                            >
                                <div
                                    class="flex items-center gap-[6px] text-[12px] font-semibold text-gray-800"
                                >
                                    <Mail
                                        class="h-[14px] w-[14px] text-gray-500"
                                    />
                                    {{ $t("vue.publish_by_email_title") }}
                                </div>
                                <p
                                    class="mt-[6px] text-[10.5px] leading-[142%] text-gray-500"
                                >
                                    {{ $t("vue.publish_by_email_desc") }}
                                </p>
                                <button
                                    type="button"
                                    class="mt-[12px] inline-flex h-[28px] items-center gap-[6px] rounded-[6px] bg-[#088BEA] px-[14px] text-[10.5px] font-medium leading-[140%] text-white transition hover:opacity-90"
                                    @click="showEmailModal = true"
                                >
                                    <Mail class="h-[12px] w-[12px]" />
                                    {{ $t("vue.send_email") }}
                                </button>
                            </div>

                            <!-- One-Click DNS Setup (Cloudflare only) -->
                            <div
                                v-if="isCloudflare"
                                class="rounded-md border border-gray-200 px-[16px] py-[16px]"
                            >
                                <div
                                    class="flex items-center gap-[6px] text-[12px] font-semibold text-gray-800"
                                >
                                    <Zap
                                        class="h-[14px] w-[14px] text-[#F38020]"
                                    />
                                    {{ $t("vue.publish_one_click_title") }}
                                </div>
                                <p
                                    class="mt-[6px] text-[10.5px] leading-[142%] text-gray-500"
                                >
                                    {{
                                        $t(
                                            "vue.publish_one_click_desc_cloudflare",
                                        )
                                    }}
                                </p>
                                <button
                                    type="button"
                                    :disabled="cloudflareSubmitting"
                                    class="mt-[12px] inline-flex h-[28px] items-center gap-[6px] rounded-[6px] bg-[#F38020] px-[14px] text-[10.5px] font-medium leading-[140%] text-white transition hover:opacity-90 disabled:opacity-60"
                                    @click="connectCloudflare"
                                >
                                    <Cloud class="h-[12px] w-[12px]" />
                                    {{ $t("vue.log_to_cloudflare") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="border-t border-gray-200 px-[21px] py-[10px]">
                    <div class="flex items-center justify-end gap-[14px]">
                        <button
                            class="inline-flex cursor-pointer items-center gap-[4px] text-[10.5px] font-medium leading-[140%] text-blue-600 hover:text-blue-700"
                            @click="showDmarcConfig = !showDmarcConfig"
                        >
                            <span>{{
                                showDmarcConfig
                                    ? $t("vue.hide_configuration")
                                    : $t("vue.configure")
                            }}</span>
                            <component
                                :is="showDmarcConfig ? ChevronUp : ChevronDown"
                                class="h-[14px] w-[9px]"
                            />
                        </button>
                        <button class="reload-btn" @click="checkDnsRecords">
                            <RefreshCw class="h-[12px] w-[12px]" />
                            {{
                                showDmarcConfig
                                    ? $t("vue.reload_dns_records")
                                    : $t("vue.reload")
                            }}
                        </button>
                    </div>
                </div>
            </ConfigSection>

            <!-- ============ SPF ============ -->
            <ConfigSection
                v-if="!showSubdomainNotice"
                :title="$t('vue.spf_configuration_title')"
                :description="$t('vue.spf_configuration_desc')"
            >
                <div class="flex items-start gap-3 px-[21px] pt-[21px]">
                    <Mail class="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.your_current_spf_record") }}
                        </div>
                        <p
                            class="mt-0.5 text-[10.5px] leading-[142%] text-gray-500"
                        >
                            {{ $t("vue.your_current_spf_record_desc") }}
                        </p>
                    </div>
                </div>

                <template v-if="spfAnalysis">
                    <div
                        class="grid grid-cols-3 gap-[14px] px-[21px] py-[18px]"
                    >
                        <div
                            class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                        >
                            <div
                                class="text-[10.5px] leading-[140%] text-gray-500"
                            >
                                {{ $t("vue.spf_dns_lookups") }}
                            </div>
                            <div
                                class="mt-1 text-[12px] font-semibold text-gray-800"
                            >
                                {{ spfDnsLookupsDisplay }}
                            </div>
                        </div>
                        <div
                            class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                        >
                            <div
                                class="text-[10.5px] leading-[140%] text-gray-500"
                            >
                                {{ $t("vue.spf_mechanisms") }}
                            </div>
                            <div
                                class="mt-1 text-[12px] font-semibold text-gray-800"
                            >
                                {{ spfAnalysis.mechanisms }}
                            </div>
                        </div>
                        <div
                            class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                        >
                            <div
                                class="text-[10.5px] leading-[140%] text-gray-500"
                            >
                                {{ $t("vue.spf_includes") }}
                            </div>
                            <div
                                class="mt-1 text-[12px] font-semibold text-gray-800"
                            >
                                {{ spfAnalysis.includes }}
                            </div>
                        </div>
                    </div>
                    <div class="px-[21px] py-[18px]">
                        <div class="notice-success">
                            <CheckCircle2
                                class="mt-[1px] h-[14px] w-[14px] shrink-0 text-[#00C951]"
                            />
                            <span>{{
                                spfAnalysis.note ||
                                $t("vue.spf_notice_configured")
                            }}</span>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="px-[21px] py-[18px]">
                        <div
                            v-if="detectedSpfRecordRaw"
                            class="rounded-md border border-gray-200 px-[14px] py-[12px] font-mono text-[11px] leading-[150%] text-gray-700 break-all"
                        >
                            {{ detectedSpfRecordRaw }}
                        </div>
                        <p v-else class="text-[10.5px] text-gray-500">
                            {{ $t("vue.not_detected") }}
                        </p>
                    </div>
                </template>

                <div class="border-t border-gray-200 px-[21px] py-[10px]">
                    <div class="flex items-center justify-end">
                        <button class="reload-btn" @click="checkDnsRecords">
                            <RefreshCw class="h-[12px] w-[12px]" />
                            {{ $t("vue.reload") }}
                        </button>
                    </div>
                </div>
            </ConfigSection>

            <!-- ============ DKIM ============ -->
            <ConfigSection
                v-if="!showSubdomainNotice"
                :title="$t('vue.dkim_setup_title')"
                :description="$t('vue.dkim_setup_desc')"
            >
                <div class="flex items-start gap-3 px-[21px] pt-[21px]">
                    <PenLine class="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.your_current_dkim_record") }}
                        </div>
                        <p
                            class="mt-0.5 text-[10.5px] leading-[142%] text-gray-500"
                        >
                            {{ $t("vue.your_current_dkim_record_desc") }}
                        </p>
                    </div>
                </div>

                <div class="px-[21px] py-[18px]">
                    <div
                        v-if="dkimSelectorList.length"
                        class="grid grid-cols-2 gap-[14px]"
                    >
                        <div
                            v-for="item in dkimSelectorList"
                            :key="item.selector"
                            class="rounded-md border border-gray-200 px-[14px] py-[12px]"
                        >
                            <div class="text-[10.5px] text-gray-500">
                                <span class="font-semibold text-gray-800"
                                    >{{ $t("vue.dkim_selector") }}:</span
                                >
                                {{ item.selector }}
                            </div>
                            <div
                                class="mt-1 text-[10.5px] leading-[142%] text-gray-600"
                            >
                                {{
                                    item.valid
                                        ? $t("vue.dkim_valid_configured")
                                        : $t("vue.dkim_invalid")
                                }}
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-[10.5px] text-gray-500">
                        {{ $t("vue.not_detected") }}
                    </p>

                    <div v-if="dkimSelectorList.length" class="mt-[14px]">
                        <div class="notice-success">
                            <CheckCircle2
                                class="mt-[1px] h-[14px] w-[14px] shrink-0 text-[#00C951]"
                            />
                            <span>{{ $t("vue.dkim_notice_configured") }}</span>
                        </div>
                    </div>
                </div>

                <div class="border-t border-gray-200 px-[21px] py-[10px]">
                    <div class="flex items-center justify-end">
                        <button class="reload-btn" @click="checkDnsRecords">
                            <RefreshCw class="h-[12px] w-[12px]" />
                            {{ $t("vue.reload") }}
                        </button>
                    </div>
                </div>
            </ConfigSection>

            <!-- ============ BIMI ============ -->
            <ConfigSection
                v-if="!showSubdomainNotice"
                :title="$t('vue.bimi_logo_title')"
                :description="$t('vue.bimi_logo_desc')"
            >
                <div class="flex items-start gap-3 px-[21px] pt-[21px]">
                    <ImageIcon class="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div>
                        <div
                            class="text-[12px] font-semibold leading-[140%] text-gray-800"
                        >
                            {{ $t("vue.your_current_bimi_record") }}
                        </div>
                        <p
                            class="mt-0.5 text-[10.5px] leading-[142%] text-gray-500"
                        >
                            {{ $t("vue.your_current_bimi_record_desc") }}
                        </p>
                    </div>
                </div>

                <div class="px-[21px] py-[18px]">
                    <div v-if="bimiStatus?.configured" class="notice-success">
                        <CheckCircle2
                            class="mt-[1px] h-[14px] w-[14px] shrink-0 text-[#00C951]"
                        />
                        <span>{{
                            bimiStatus.note ||
                            $t("vue.bimi_properly_configured")
                        }}</span>
                    </div>
                    <div
                        v-else
                        class="rounded-md border border-gray-200 px-[14px] py-[12px] text-[10.5px] leading-[142%] text-gray-700"
                    >
                        {{ $t("vue.bimi_not_detected") }}
                    </div>
                </div>

                <div class="border-t border-gray-200 px-[21px] py-[10px]">
                    <div class="flex items-center justify-end">
                        <button class="reload-btn" @click="checkDnsRecords">
                            <RefreshCw class="h-[12px] w-[12px]" />
                            {{ $t("vue.reload") }}
                        </button>
                    </div>
                </div>
            </ConfigSection>
        </div>

        <!--
            Send-instructions popup = NEW UI (Figma modal) reusing the EXISTING
            working EmailInstructions logic verbatim. Only the presentation changed.
            TODO(claude-code): align inner fields/styling with Figma modal (image 2),
            ideally by adding a `display="modal"` mode to EmailInstructions.vue so the
            same component renders inline (Wizard) or as a dialog (here). Also wire
            close-on-success once EmailInstructions exposes a success emit.
        -->
        <Teleport to="body">
            <Transition name="fade">
                <div
                    v-if="showEmailModal"
                    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    @click.self="showEmailModal = false"
                    @keydown.esc="showEmailModal = false"
                >
                    <div
                        class="w-full max-w-[627px] rounded-xl bg-white p-11 shadow-[0_15px_35px_rgba(0,0,0,0.15)]"
                        role="dialog"
                        aria-modal="true"
                    >
                        <EmailInstructions
                            :domain="reportedDomain.domain"
                            :txt-record="txtRecord"
                            title-key="vue.email_instructions_to_developer"
                        />

                        <div class="mt-6 flex justify-end">
                            <button
                                type="button"
                                class="rounded-md border border-[#088bea] px-3.5 py-2.5 text-[10.5px] font-medium text-[#088bea] transition-colors hover:bg-[#088bea]/5"
                                @click="showEmailModal = false"
                            >
                                {{ $t("vue.cancel") }}
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import route from "ziggy-js";
import {
    computed,
    nextTick,
    onMounted,
    reactive,
    ref,
    watch,
    type PropType,
} from "vue";
import { router } from "@inertiajs/vue3";
import { useDmarcRecordPreparer } from "@/composables/useDmarcRecordPreparer";
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import {
    ChevronDown,
    ChevronUp,
    RefreshCw,
    ShieldCheck,
    Mail,
    PenLine,
    Image as ImageIcon,
    CheckCircle2,
    Pencil,
    Zap,
    Cloud,
} from "lucide-vue-next";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";
import { useDomainConnectApply } from "@/composables/useDomainConnectApply";
import { buildDomainReportTabs } from "@/Pages/Reports/utils/buildDomainReportTabs";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import ConfigSection from "@/components/Special/ConfigSection.vue";
import ManagedDmarcConfigurator from "@/components/Special/ManagedDmarcConfigurator.vue";
import EmailInstructions from "@/components/Special/EmailInstructions.vue";
import {
    getDomainStatusBadgeClass,
    getDomainStatusDotClass,
    getDomainStatusTranslationKey,
} from "@/utils/domainStatus";
import type IDomain from "@/types/Domains/IDomain.ts";

const { t, locale } = useTranslations();

interface ReportedDomain {
    domain: string;
    active: boolean;
    created_at?: string;
    dmarcPolicy: string;
    dmarcRecord?: string | null;
    account: string;
    parked?: boolean;
}

interface DomainInfo {
    dnsProvider?: string | null;
    spfRecord?: string | null;
    dmarcRecord?: string | null;
    dkimSelectors?: string[] | null;
}

// --- Optional analysis props (controller -> Inertia -> props) ---
interface SpfAnalysis {
    dnsLookups: number;
    dnsLookupsLimit: number;
    mechanisms: number;
    includes: number;
    note?: string;
}

interface DkimSelectorStatus {
    selector: string;
    valid: boolean;
}

interface BimiStatus {
    configured: boolean;
    record?: string | null;
    note?: string;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    domainInfo: {
        type: Object as PropType<DomainInfo>,
        required: false,
        default: () => ({}),
    },
    isCloudflare: { type: Boolean, required: false, default: true },
    isSubdomainRecord: { type: Boolean, required: false, default: false },
    parentDomain: {
        type: Object as PropType<IDomain | null>,
        required: false,
        default: null,
    },
    parentDomainName: {
        type: String as PropType<string | null>,
        required: false,
        default: null,
    },
    parentDomainExistInDmarceye: {
        type: Boolean,
        required: false,
        default: false,
    },
    // TODO(backend): provide via controller -> Inertia
    spfAnalysis: {
        type: Object as PropType<SpfAnalysis | null>,
        required: false,
        default: null,
    },
    dkimSelectorsStatus: {
        type: Array as PropType<DkimSelectorStatus[] | null>,
        required: false,
        default: null,
    },
    bimiStatus: {
        type: Object as PropType<BimiStatus | null>,
        required: false,
        default: null,
    },
});

const tabs = computed(() =>
    buildDomainReportTabs(
        props.reportedDomain.domain,
        props.reportedDomain?.parked ?? false,
        t,
    ),
);

const domainState = reactive({
    active: false,
    parked: false,
    dmarcPolicy: "none",
    dmarcRecord: (props.reportedDomain.dmarcRecord ??
        props.domainInfo?.dmarcRecord ??
        null) as string | null,
});

const detectedSpfRecordRaw = ref<string | null>(
    props.domainInfo?.spfRecord ?? null,
);
const detectedDkimSelectors = ref<string[]>(
    props.domainInfo?.dkimSelectors ?? [],
);

const showDmarcConfig = ref(false);
const showInheritedDmarcConfig = ref(false);
const showEmailModal = ref(false);
const recordStatusCheckedAt = ref("-");

onMounted(() => {
    domainState.active = props.reportedDomain.active;
    domainState.parked = props.reportedDomain.parked ?? false;
    domainState.dmarcPolicy = props.reportedDomain.dmarcPolicy;
    domainState.dmarcRecord =
        props.reportedDomain.dmarcRecord ??
        props.domainInfo?.dmarcRecord ??
        null;
    recordStatusCheckedAt.value = formatRecordStatusCheckedAt(new Date());
    nextTick(() => {
        lastPreparedRecord.value = txtRecord.value;
    });
});

const domainStatusBadgeClass = computed(() => {
    return getDomainStatusBadgeClass(domainState);
});

const domainStatusLabel = computed(() => {
    return t(getDomainStatusTranslationKey(domainState));
});

const { preparedRecord } = useDmarcRecordPreparer(
    computed(() => domainState.dmarcRecord),
);

const txtRecord = ref(preparedRecord.value);
const lastPreparedRecord = ref(preparedRecord.value);

const { apply: connectCloudflare, submitting: cloudflareSubmitting } =
    useDomainConnectApply(
        () => props.reportedDomain.domain,
        txtRecord,
        "dmarc-management",
    );

watch(
    preparedRecord,
    async (value) => {
        if (txtRecord.value === lastPreparedRecord.value) {
            txtRecord.value = value;
            await nextTick();
            lastPreparedRecord.value = txtRecord.value;
        } else {
            lastPreparedRecord.value = value;
        }
    },
    { immediate: true },
);

const policyDisplay = computed(() => {
    const currentPolicy = domainState.dmarcPolicy?.trim() || "";
    if (currentPolicy === "") return "none";
    return currentPolicy;
});

const policyDisplayLabel = computed(() => {
    const value = policyDisplay.value.toLowerCase();
    if (value === "reject") return "reject";
    if (value === "quarantine") return "quarantine";
    if (value === "none") return "none";
    return policyDisplay.value;
});

const policyBadgeClass = computed(() => {
    if (policyDisplay.value === "reject") return "policy-reject";
    if (policyDisplay.value === "quarantine") return "policy-quarantine";
    return "policy-none";
});

// --- DMARC summary stats (derived from the live record) ---
const dmarcPolicyStat = computed(() => policyDisplayLabel.value);

const dmarcPctStat = computed(() => {
    const match = txtRecord.value.match(/pct\s*=\s*(\d+)/i);
    return `${match ? match[1] : "100"}%`;
});

const dmarcReportsCount = computed(() => {
    const match = txtRecord.value.match(/rua\s*=\s*([^;]+)/i);
    if (!match) return 0;
    return (match[1] ?? "").split(",").filter((address) => address.trim())
        .length;
});

// --- SPF summary stats ---
const spfDnsLookupsDisplay = computed(() => {
    const spf = props.spfAnalysis;
    if (!spf) return "—";
    const remaining = Math.max(spf.dnsLookupsLimit - spf.dnsLookups, 0);
    return t("vue.spf_dns_lookups_value", { used: spf.dnsLookups, remaining });
});

// --- DKIM selectors (prop status wins, else detected/static selectors) ---
const dkimSelectorList = computed<DkimSelectorStatus[]>(() => {
    if (props.dkimSelectorsStatus?.length) return props.dkimSelectorsStatus;
    const selectors = detectedDkimSelectors.value.length
        ? detectedDkimSelectors.value
        : (props.domainInfo?.dkimSelectors ?? []);
    return selectors.map((selector) => ({ selector, valid: true }));
});

const hasDmarcRecord = computed(() => {
    const record = domainState.dmarcRecord;
    return record !== null && String(record).trim() !== "";
});

const showSubdomainNotice = computed(
    () => props.isSubdomainRecord === true && hasDmarcRecord.value === false,
);

const parentDomainDisplayName = computed(
    () => props.parentDomain?.domain ?? props.parentDomainName ?? "",
);

const visitParentDomain = () => {
    if (props.parentDomain === null) return;
    router.get(
        route("reports.dmarc-management.show", {
            domain: props.parentDomain.domain,
        }),
    );
};

const dnsCheckingInProgress = ref(false);

async function checkDnsRecords() {
    if (dnsCheckingInProgress.value) return;
    dnsCheckingInProgress.value = true;

    try {
        const response = await axios.get(
            route("domains.check-dns", { domain: props.reportedDomain.domain }),
        );
        if (!response.data.success) {
            console.warn("DNS checking failed:", response.data.error);
            return;
        }

        const result = response.data.result;
        domainState.active = result.active;
        domainState.dmarcPolicy = result.dmarc_policy;
        domainState.dmarcRecord = result.dmarc_record ?? null;

        detectedSpfRecordRaw.value = result.spf_record ?? null;
        detectedDkimSelectors.value = Array.isArray(result.dkim_selectors)
            ? result.dkim_selectors
            : [];
        recordStatusCheckedAt.value = formatRecordStatusCheckedAt(new Date());

        create({ title: t("vue.record_checked"), type: "success" });
    } catch (error) {
        console.error("Failed to check DNS records:", error);
    } finally {
        dnsCheckingInProgress.value = false;
    }
}

function formatRecordStatusCheckedAt(date: Date): string {
    return new Intl.DateTimeFormat(locale.value, {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
}
</script>

<style scoped>
.step-badge {
    display: inline-flex;
    height: 20px;
    width: 20px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background-color: #088bea;
    font-size: 10px;
    font-weight: 600;
    color: #fff;
}

.reload-btn {
    display: inline-flex;
    height: 28px;
    cursor: pointer;
    align-items: center;
    gap: 6px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background-color: #fff;
    padding-inline: 14px;
    font-size: 10.5px;
    font-weight: 500;
    line-height: 140%;
    color: #374151;
    transition: background-color 0.15s ease;
}

.reload-btn:hover {
    background-color: #f9fafb;
}

.notice-success {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    border-radius: 6px;
    border: 1px solid #bbf7d0;
    background-color: #f0fdf4;
    padding: 10px 14px;
    font-size: 10.5px;
    line-height: 142%;
    color: #276749;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
